import { CheckCircle2, XCircle } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataTable, type DataTableColumn } from "../../../components/table/data-table";
import type { SyncPointItem } from "../types/syncPoints.types";

interface Props {
  items: SyncPointItem[];
  loading: boolean;
  error: string | null;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onRetry: () => void;
}
export function SyncPointsTable({
  items,
  loading,
  error,
  page,
  totalPages,
  onPageChange,
  onRetry,
}: Props) {
  const { t } = useTranslation();
  const columns = useMemo<DataTableColumn<SyncPointItem>[]>(
    () => [
      {
        key: "user",
        title: t("sync_points.user"),
        render: (item) => (
          <>
            <p className="font-semibold">{item.userName}</p>
            <p className="text-xs text-gray-500">{item.userEmail}</p>
          </>
        ),
      },
      {
        key: "available",
        title: t("sync_points.available_points"),
        render: (item) => `${item.availablePoints.toLocaleString()} pts`,
      },
      {
        key: "wallet",
        title: t("sync_points.wallet_transaction_points"),
        render: (item) => `${item.walletTransactionPoints.toLocaleString()} pts`,
      },
      {
        key: "status",
        title: t("sync_points.wallet_status"),
        render: (item) =>
          item.hasWalletTransaction ? (
            <span className="inline-flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" />
              {t("sync_points.in_wallet_transaction")}
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 text-gray-500">
              <XCircle className="h-5 w-5" />
              {t("sync_points.not_in_wallet_transaction")}
            </span>
          ),
      },
    ],
    [t],
  );
  return (
    <DataTable
      columns={columns}
      dataSource={items}
      rowKey="userId"
      loading={loading}
      error={error}
      onRetry={onRetry}
      emptyLabel={t("sync_points.empty")}
      pagination={{ page, totalPages, onPageChange }}
    />
  );
}
